import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Platform, Pressable, ScrollView, StatusBar, StyleSheet } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { useRouter } from 'expo-router'

import { JoinPremiumSheet, useJoinPremiumSheet, VideoPlayer } from '../../components'
import { CompletedChapterSheet, useCompletedChapterSheet } from '../../components/CompletedChapterSheet'
import { OutOfOrderSheet, useOutOfOrderSheet } from '../../components/OutOfOrderSheet'
import { DS } from '../../design-system'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { ChapterModelType } from '../../models/api-store'

import { fontFamily } from '../../theme'
import { Chapters } from './sections/Chapters'
import { Overview } from './sections/Overview'
import { useRemoteConfig } from '../../../module/remote-config/useRemoteConfig'
import { WebWrapper } from '@/components/WebWrapper'

export interface VideoLesson {
  isPlaying: boolean
  currentPosition?: number
  completed?: boolean
}

interface VideoPlayerRef {
  resume: () => void
  pause: () => void
  seek: (time: number) => void
}

export const LessonOverviewScreen = observer(() => {
  const router = useRouter()
  const {
    lessons: {
      lessonSelected,
      updateUserChapterProgress,
      setProgressChapter,
      setDurationChapter,
      setCurrentChapter,
      setSelectedLesson,
      currentChapter,
      lessons
    },
    analytics: { setEvent },
    memberships: { isSubscribed }
  } = useStore()

  const videoRef = useRef<VideoPlayerRef>(null)

  const { handleOpenOutOfOrderSheet, outOfOrderSheetModalRef, closeBottomModal } = useOutOfOrderSheet()
  const {
    handleCompletedChapterSheet,
    completedChapterSheetRef,
    closeBottomModal: closeBottomModalCompletedChapter
  } = useCompletedChapterSheet()
  const {
    handleJoinPremiumSheet,
    joinPremiumSheetRef,
    closeBottomModal: closeBottomModalJoinPremium
  } = useJoinPremiumSheet()

  const [currentTab, setCurrentTab] = useState(0)
  const [preChapterSelectedId, setPreChapterSelectedId] = useState<string | undefined>(undefined)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const isLearningUnlocked = useRemoteConfig((state) => state.is_learning_unlocked)

  const getEarliestUnwatchedChapter = useCallback(
    (chapters: ChapterModelType[]) => {
      const firstUnwatchedChapter = chapters.find(
        (chapter) => !chapter.completed && (chapter.membership === 'free' || isSubscribed)
      )

      // If none is found, return the last chapter based on the subscription condition.
      const fallbackChapter = isSubscribed
        ? chapters[chapters.length - 1] // Last chapter
        : [...chapters].reverse().find((chapter) => chapter.membership === 'free') // Last free chapter

      const selectedChapter = firstUnwatchedChapter || fallbackChapter

      setCurrentChapter(selectedChapter as ChapterModelType)
    },
    [setCurrentChapter, isSubscribed]
  )

  const isLessonStarting = (chapters: ChapterModelType[]) => {
    const allChaptersStartAtZero = chapters.every((chapter) => chapter.currentPosition === 0)

    if (allChaptersStartAtZero) {
      setEvent(events.videoLessons.lesson_started, {
        lesson_name: lessonSelected?.name,
        lesson_id: lessonSelected?.lessonId,
        chapter_name: currentChapter?.name,
        chapter_id: currentChapter?._id
      })
    }
  }

  const isLessonFinished = (chapters: ChapterModelType[]) => {
    const allChaptersAreCompleted = chapters.every((chapter) => chapter.completed)

    if (allChaptersAreCompleted) {
      setEvent(events.videoLessons.lesson_completed, {
        lesson_name: lessonSelected?.name,
        lesson_id: lessonSelected?.lessonId,
        chapter_name: currentChapter?.name,
        chapter_id: currentChapter?._id
      })
    }
  }

  const handleEarliestUnwatchedVideo = useCallback(() => {
    if (lessonSelected?.chapters) {
      getEarliestUnwatchedChapter(lessonSelected?.chapters as ChapterModelType[])
      isLessonStarting(lessonSelected?.chapters as ChapterModelType[])
      isLessonFinished(lessonSelected?.chapters as ChapterModelType[])
    }

    closeBottomModal()
  }, [lessonSelected, getEarliestUnwatchedChapter, closeBottomModal])

  useEffect(() => {
    handleEarliestUnwatchedVideo()
  }, [lessonSelected, handleEarliestUnwatchedVideo, isSubscribed])

  const tabs = [
    { id: 0, label: 'Overview' },
    { id: 1, label: `Chapters (${lessonSelected?.chapters?.length})` }
  ]

  const handleTabPress = (tabId: number) => {
    setCurrentTab(tabId)
  }

  const handleOnBack = () => {
    if (router.canDismiss()) {
      return router.dismiss()
    }
    if (router.canGoBack()) {
      return router.back()
    }
    router.dismissTo('/(root)/(tabs)/learning')
  }

  const handleOnPressChapter = (_id: string | undefined | null) => {
    if (!_id) return

    const chapters = lessonSelected?.chapters
    if (!chapters) return

    const currentChapterIndex = chapters.findIndex((chapter) => chapter._id === _id)
    if (currentChapterIndex === -1) return

    const { membership } = chapters[currentChapterIndex]

    if (!isLearningUnlocked && membership !== 'free' && !isSubscribed) {
      handleJoinPremiumSheet()
      return
    }

    const hasIncompletePrevious = chapters.slice(0, currentChapterIndex).some((chapter) => !chapter.completed)

    if (hasIncompletePrevious) {
      setPreChapterSelectedId(_id)
      handleOpenOutOfOrderSheet()
      return
    }

    handleSetCurrentChapter(_id)
  }

  const handleSetCurrentChapter = (_id?: string | undefined | null) => {
    const chapterId = _id || preChapterSelectedId
    if (lessonSelected?.chapters && chapterId) {
      const chapter = lessonSelected?.chapters.find((x) => x._id === chapterId)

      if (chapter) {
        setCurrentChapter(chapter as ChapterModelType)

        if (chapter.completed) {
          handleSeekVideo(0)
          handleResume()
        } else {
          handleResume()
        }
      }
    }
    closeBottomModal()
  }

  const handleCurrentVideo = (params: VideoLesson, _id: string | undefined | null) => {
    if (!(_id && lessonSelected?.lessonId)) return

    const chapter = lessonSelected.chapters?.find((chapter) => chapter._id === _id)
    if (!chapter) return

    const { completed, currentPosition = 0, isPlaying } = params
    const visibility = chapter.visibility

    if (currentPosition !== chapter.currentPosition || completed !== chapter.completed) {
      setProgressChapter(_id, currentPosition, completed)
    }

    if (isPlaying === false) {
      const currentCompleted = completed || chapter.completed
      updateUserChapterProgress(
        { completed: !!currentCompleted, currentPosition, visibility: !!visibility },
        lessonSelected.lessonId,
        _id
      )
    }

    setIsVideoPlaying(isPlaying)

    if (completed && hasNextChapter(_id)) {
      handleCompletedChapterSheet()
    }
  }

  const hasNextChapter = (_id: string | undefined | null): boolean => {
    if (!_id) return false

    const chapters = lessonSelected?.chapters
    if (!chapters) return false

    const currentChapterIndex = chapters.findIndex((chapter) => chapter._id === _id)
    if (currentChapterIndex === -1) return false

    for (let i = currentChapterIndex + 1; i < chapters.length; i++) {
      const chapter = chapters[i]
      if (chapter.membership === 'free' || isSubscribed) {
        return true
      }
    }

    if (
      currentChapterIndex + 1 === chapters.length &&
      (chapters[currentChapterIndex].membership === 'free' || isSubscribed)
    ) {
      return true
    }

    handleJoinPremiumSheet()
    return false
  }

  const getNextChapter = (_id: string | undefined | null) => {
    if (!_id) return

    const chapters = lessonSelected?.chapters
    if (!chapters) return

    const currentChapterIndex = chapters.findIndex((chapter) => chapter._id === _id)
    if (currentChapterIndex === -1) return

    const nextIncompleteChapter = chapters.find((chapter) => !chapter.completed)

    if (nextIncompleteChapter?.membership === 'free' || isSubscribed) {
      return nextIncompleteChapter
    }
  }

  const checkIfIsLessonCompleted = () => {
    const chapters = lessonSelected?.chapters
    if (!chapters) return false

    return chapters.every((chapter) => chapter.completed)
  }

  const checkIfIsAllLessonsCompleted = () => {
    if (!lessons || lessons.length === 0) return false

    return lessons.every((lesson) => {
      return lesson.chapters?.every((chapter) => chapter.completed)
    })
  }

  const handleOnCompleteProgressBar = () => {
    if (checkIfIsLessonCompleted()) {
      const lessonId = lessonSelected?.lessonId

      if (!(lessons && lessonId)) return

      const currentLessonIndex = lessons.findIndex((lesson) => lesson.lessonId === lessonId)
      if (currentLessonIndex === -1) return

      const nextIncompleteLesson = lessons
        .slice(currentLessonIndex + 1)
        .find((lesson) => !lesson.chapters?.every((chapter) => chapter.completed))

      if (nextIncompleteLesson?.lessonId) {
        setSelectedLesson(nextIncompleteLesson.lessonId)
      }
    } else {
      const nextChapter = getNextChapter(currentChapter?._id)

      if (nextChapter) {
        setCurrentChapter(nextChapter as ChapterModelType)
      }
    }

    closeBottomModalCompletedChapter()
  }

  const handleOnPressOkCompletedChapter = () => {
    if (checkIfIsAllLessonsCompleted()) {
      handleOnBack()
    } else {
      handleOnCompleteProgressBar()
    }
  }

  const handleOnGetPremium = (subscriber: boolean) => {
    //setUserSubscriber(subscriber)
    closeBottomModalJoinPremium()
    if (subscriber) {
      //JoinPremiumScreen
      router.navigate('/(root)/(tabs)/membership')
    }
  }

  const handleSeekVideo = (time: number) => {
    videoRef.current?.seek(time)
  }

  const handleResume = () => {
    videoRef.current?.resume()
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle={'dark-content'} />
      <WebWrapper>
        <DS.Frame row style={styles.header} alignItems="center" justifyContent="space-between">
          <Pressable onPress={() => handleOnBack()}>
            <DS.Frame>
              <DS.Icon name="Arrow---left" size={24} color={'Global/Black'} />
            </DS.Frame>
          </Pressable>
          <DS.Frame>
            <DS.Text variant="Body Medium/Medium" style={{ fontFamily: fontFamily.GTAmericaStandardRegular }}>
              {lessonSelected?.name}
            </DS.Text>
          </DS.Frame>
          <DS.Frame />
        </DS.Frame>

        <DS.Frame style={styles.video}>
          {currentChapter != null && (
            <VideoPlayer
              ref={videoRef}
              chapter={currentChapter}
              setCurrentVideo={handleCurrentVideo}
              setDurationChapter={setDurationChapter}
            />
          )}
        </DS.Frame>

        <OutOfOrderSheet
          bottomSheetModalRef={outOfOrderSheetModalRef}
          okayPress={() => handleEarliestUnwatchedVideo()}
          ignorePress={() => handleSetCurrentChapter()}
        />

        <CompletedChapterSheet
          bottomSheetModalRef={completedChapterSheetRef}
          okayPress={() => handleOnPressOkCompletedChapter()}
          ignorePress={() => closeBottomModalCompletedChapter()}
          chapterName={currentChapter?.name}
          chapterNumber={currentChapter?.order}
          nextChapterName={getNextChapter(currentChapter?._id)?.name}
          isLessonCompleted={checkIfIsLessonCompleted}
          isAllLessonCompleted={checkIfIsAllLessonsCompleted}
          onComplete={handleOnCompleteProgressBar}
        />

        <JoinPremiumSheet
          bottomSheetModalRef={joinPremiumSheetRef}
          okayPress={handleOnGetPremium}
          ignorePress={handleOnGetPremium}
        />

        <DS.Frame style={styles.tabs} row alignItems="center">
          {tabs.map((tab) => (
            <Pressable key={tab.id} onPress={() => handleTabPress(tab.id)} style={{ flex: 1 }}>
              <DS.Frame style={currentTab === tab.id && styles.selected} justifyContent="center" alignItems="center">
                <DS.Text variant="Body Small/Medium" style={[styles.pill]}>
                  {tab.label}
                </DS.Text>
              </DS.Frame>
            </Pressable>
          ))}
        </DS.Frame>

        <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
          {currentTab === 0 ? (
            <Overview data={lessonSelected} />
          ) : (
            <Chapters
              chapters={lessonSelected?.chapters as ChapterModelType[]}
              onChapterPress={handleOnPressChapter}
              currentChapter={currentChapter as ChapterModelType}
              isVideoPlaying={isVideoPlaying}
              isUserSubscriber={isSubscribed}
            />
          )}
        </ScrollView>
      </WebWrapper>
    </SafeAreaView>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream'],
    paddingTop: Platform.OS === 'android' ? StatusBar.currentHeight : 0
  },
  header: {
    padding: 24
  },
  video: {
    backgroundColor: 'gray',
    width: '100%',
    height: 200
  },
  tabs: {
    backgroundColor: DS.palette['Brand/Light Grey'],
    margin: 24,
    minHeight: 42,
    borderRadius: 32
  },
  pill: {
    fontFamily: fontFamily.GTAmericaStandardRegular,
    fontSize: 14
  },
  selected: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 32,
    flex: 1,
    margin: 3
  }
})
